import { Car, Coffee, ListChecks, LogIn, LogOut, MapPin, Play, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { getObjectColors } from '../../../utils/objectColors';
import { StatusDot } from '../StatusDot';
import type { PlanningTask } from '../../../types/dashboard';
import { formatHours, minToHHMM } from '../../../utils/dashboardData';
import type { ClockState } from '../../../store/useTimeclockStore';
import { formatDuration } from '../../../utils/timeclockTotals';
import { useClockInWindow } from '../../../hooks/useClockInWindow';
import { TripActions } from './TripActions';

interface NowHeroProps {
  currentTask: PlanningTask | null;
  upcomingFirst: PlanningTask | null;
  nowMinutes: number;
  /** Stamped work time today, counted like the time clock page (FACF-87). */
  workedMinutes: number;
  /** Soll from the day's planning. */
  targetMinutes: number;
  timeclockState: ClockState;
  /** Inside the clock-in window — gates only clocking in, never clock-out or breaks. */
  clockInAllowed: boolean;
  /** An earlier day still lacks its clock-out; the backend refuses a new clock-in until fixed. */
  clockInBlocked: boolean;
  busy: boolean;
  onAbsenceToday: boolean;
  absenceLabel?: string;
  /** Unconfirmed checklist items across today's tasks. */
  openChecklistCount?: number;
  /** How many of them are left over from earlier days — named separately in the pill. */
  openPastChecklistCount?: number;
  /** Planned vehicle of the day, e.g. "W 481 GL (Fahrer)" (FACF-118); the running trip shows below. */
  vehicleLabel?: string | null;
  onClockIn: () => void;
  onClockOut: () => void;
  onBreakStart: () => void;
  onBreakEnd: () => void;
}

/** Status word next to the dot — the state must be readable, not only a colour. */
const STATUS_TEXT_TONE: Record<ClockState, string> = {
  in: 'text-emerald-300',
  break: 'text-amber-300',
  idle: 'text-orange-300',
  out: 'text-slate-300',
};

export function NowHero({
  currentTask,
  upcomingFirst,
  nowMinutes,
  workedMinutes,
  targetMinutes,
  timeclockState,
  clockInAllowed,
  clockInBlocked,
  busy,
  onAbsenceToday,
  absenceLabel,
  openChecklistCount = 0,
  openPastChecklistCount = 0,
  vehicleLabel = null,
  onClockIn,
  onClockOut,
  onBreakStart,
  onBreakEnd,
}: NowHeroProps) {
  const { t } = useTranslation('dashboard');
  const clockInWindow = useClockInWindow();

  const onAbsence = onAbsenceToday;
  const objectKey = currentTask?.objectId ?? currentTask?.objectName ?? null;
  const objectName = currentTask?.objectName ?? currentTask?.objectShortName ?? '';
  const colors = currentTask ? getObjectColors(objectKey ?? objectName) : null;

  const startMin = currentTask ? timeMin(currentTask.startTime) : 0;
  const endMin = currentTask ? timeMin(currentTask.endTime) : 0;
  const taskTotal = Math.max(1, endMin - startMin);
  const taskProgress = currentTask
    ? Math.min(100, Math.max(0, ((nowMinutes - startMin) / taskTotal) * 100))
    : 0;

  const dayProgress = targetMinutes > 0
    ? Math.min(100, (workedMinutes / targetMinutes) * 100)
    : 0;

  const heroAccent =
    onAbsence
      ? '#48bfe3'
      : timeclockState === 'idle'
      ? '#f97316'
      : timeclockState === 'break'
      ? '#f59e0b'
      : currentTask && colors
      ? colors.color
      : '#10b981';

  const showPulse = timeclockState === 'in';
  const canClockIn = timeclockState === 'idle' || timeclockState === 'out';
  const hasPlanToday = Boolean(currentTask || upcomingFirst);

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-ink dark:bg-slate-950 text-white shadow-lg"
      role="region"
      aria-label={t('now.label')}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: heroAccent }}
        aria-hidden
      />
      <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr_1fr] gap-0 lg:gap-px">
        {/* Main panel */}
        <div className="p-5 sm:p-6 lg:p-7">
          <div className="flex items-center gap-2 mb-3">
            <StatusDot
              status={
                onAbsence ? 'absent' : timeclockState === 'idle' ? 'missing' : timeclockState
              }
              pulse={showPulse}
              size="md"
            />
            {!onAbsence && (
              <span className={`text-[12px] font-semibold ${STATUS_TEXT_TONE[timeclockState]}`}>
                {t(`now.status.${timeclockState}`)}
              </span>
            )}
            <span className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-400">
              {t('now.label')} · {minToHHMM(nowMinutes)}
            </span>
            {!onAbsence && vehicleLabel && (
              <span
                className="ml-auto inline-flex items-center gap-1 rounded bg-white/10 px-2 py-0.5 font-mono-num text-[11px] text-slate-200"
                title={t('now.plannedVehicle', { plate: vehicleLabel })}
              >
                <Car className="w-3.5 h-3.5" aria-hidden />
                {vehicleLabel}
              </span>
            )}
          </div>

          {onAbsence ? (
            <>
              <div className="text-2xl sm:text-3xl font-semibold">{t('now.onVacation')}</div>
              {absenceLabel && (
                <div className="mt-2 text-sm text-slate-300">{absenceLabel}</div>
              )}
            </>
          ) : currentTask ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-1 font-mono-num">
                {t('now.youAreAt')}
              </div>
              <div className="text-2xl sm:text-3xl font-semibold truncate" title={objectName}>
                {objectName}
              </div>
              {currentTask.taskName && (
                <div className="mt-1 text-sm text-slate-300 truncate">
                  {currentTask.serviceName ? `${currentTask.serviceName} – ` : ''}
                  {currentTask.taskName}
                </div>
              )}
              <div className="mt-4 flex items-center gap-3 text-sm text-slate-300 font-mono-num">
                <MapPin className="w-4 h-4 text-slate-400" aria-hidden />
                <span>
                  {currentTask.startTime?.substring(0, 5)} – {currentTask.endTime?.substring(0, 5)}
                </span>
                <span className="text-slate-500">·</span>
                <span>{formatHours(taskTotal)}</span>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden" aria-hidden>
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${taskProgress}%`, background: heroAccent }}
                />
              </div>
              {timeclockState === 'idle' && (
                <div className="mt-3 text-xs font-medium text-orange-300">
                  {t('now.notClockedInHint')}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-2xl sm:text-3xl font-semibold">{headline(timeclockState, t)}</div>
              {upcomingFirst && (
                <div className="mt-2 text-sm text-slate-300">
                  {t('now.nextTaskAt', {
                    object: upcomingFirst.objectName ?? '',
                    time: upcomingFirst.startTime?.substring(0, 5) ?? '',
                  })}
                </div>
              )}
              {timeclockState === 'idle' && hasPlanToday && (
                <div className="mt-2 text-xs font-medium text-orange-300">
                  {t('now.notClockedInHint')}
                </div>
              )}
            </>
          )}
          {!onAbsence && canClockIn && clockInBlocked && (
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-0.5" aria-hidden />
              <span>{t('timeclock:openDays.blocksClockIn')}</span>
            </div>
          )}
          {!onAbsence && canClockIn && !clockInAllowed && (
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-0.5" aria-hidden />
              <span>
                {t('timeclock.outsideHours', { start: clockInWindow.start, end: clockInWindow.end })}
              </span>
            </div>
          )}
          {!onAbsence && canClockIn && clockInAllowed && clockInWindow.beforeWorkCount && (
            <div className="mt-3 text-xs text-amber-300">
              {t('timeclock.earlyClockInHint', { time: clockInWindow.workCountStart })}
            </div>
          )}
        </div>

        {/* Day totals */}
        <div className="border-t lg:border-t-0 lg:border-l border-white/10 p-5 sm:p-6 bg-white/[0.02]">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-2">
            {t('now.dayTotal')}
          </div>
          <div className="font-mono-num text-3xl font-semibold">
            {formatDuration(workedMinutes)}
            {targetMinutes > 0 && (
              <span className="text-base text-slate-400 font-normal">
                {' '}
                / {formatDuration(targetMinutes)}
              </span>
            )}
          </div>
          <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden" aria-hidden>
            <div
              className="h-full bg-emerald-400 transition-all duration-500"
              style={{ width: `${dayProgress}%` }}
            />
          </div>
          <div className="mt-2 text-[11px] text-slate-400">
            {targetMinutes > 0 ? t('now.stampedOfTarget') : t('now.stamped')}
          </div>
        </div>

        {/* Action panel */}
        <div className="border-t lg:border-t-0 lg:border-l border-white/10 p-5 sm:p-6 flex flex-col justify-center gap-2">
          {!onAbsence && openChecklistCount > 0 && (
            <div className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-amber-500/15 px-3 py-1.5 text-[11px] font-medium text-amber-200">
              <ListChecks className="w-3.5 h-3.5" aria-hidden />
              <span>
                {t('checklist.openCount', { count: openChecklistCount })}
                {openPastChecklistCount > 0 && (
                  <span className="text-amber-300/80">
                    {' '}
                    {t('checklist.openPastCount', { count: openPastChecklistCount })}
                  </span>
                )}
              </span>
            </div>
          )}
          {onAbsence ? (
            <div className="text-sm text-slate-300">—</div>
          ) : timeclockState === 'idle' ? (
            <button
              type="button"
              onClick={onClockIn}
              disabled={busy || !clockInAllowed || clockInBlocked}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-status-full hover:bg-emerald-500 px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LogIn className="w-4 h-4" aria-hidden />
              {t('now.clockIn')}
            </button>
          ) : timeclockState === 'break' ? (
            <>
              <button
                type="button"
                onClick={onBreakEnd}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-status-full hover:bg-emerald-500 px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-50"
              >
                <Play className="w-4 h-4" aria-hidden />
                {t('now.resumeBreak')}
              </button>
              <button
                type="button"
                onClick={onClockOut}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-status-conflict/90 hover:bg-status-conflict px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
              >
                <LogOut className="w-4 h-4" aria-hidden />
                {t('now.clockOut')}
              </button>
            </>
          ) : timeclockState === 'in' ? (
            <>
              <button
                type="button"
                onClick={onBreakStart}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
              >
                <Coffee className="w-4 h-4" aria-hidden />
                {t('now.break')}
              </button>
              <button
                type="button"
                onClick={onClockOut}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-status-conflict/90 hover:bg-status-conflict px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
              >
                <LogOut className="w-4 h-4" aria-hidden />
                {t('now.clockOut')}
              </button>
            </>
          ) : (
            <div className="text-sm text-slate-300 font-mono-num">
              {formatDuration(workedMinutes)}
            </div>
          )}
          {!onAbsence && <TripActions clockedIn={timeclockState === 'in' || timeclockState === 'break'} tone="dark" />}
        </div>
      </div>
    </div>
  );
}

function headline(state: ClockState, t: (key: string) => string): string {
  switch (state) {
    case 'idle':
      return t('now.notClockedIn');
    case 'in':
      return t('now.clockedInNoTask');
    case 'break':
      return t('now.onBreak');
    case 'out':
      return t('now.clockedOut');
  }
}

function timeMin(hhmm?: string | null): number {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map((v) => Number(v) || 0);
  return h * 60 + m;
}
